import type { LangCode, TranscriptLine } from "@scoach/types";
import { useCallback, useEffect, useRef, useState } from "react";

import { effectiveGeminiKey, ensureGeminiKey } from "../../../../lib/geminiKey.ts";
import { api } from "../../../../lib/http.ts";

const LIVE_MODELS = [
  // Gemini Live requires native-audio models for bidirectional WebSocket streaming.
  { ver: "v1beta", model: "models/gemini-2.5-flash-native-audio-latest" },
  { ver: "v1beta", model: "models/gemini-2.5-flash-native-audio-preview-12-2025" },
  { ver: "v1alpha", model: "models/gemini-2.5-flash-native-audio-latest" },
];

export interface UseGeminiLiveOptions {
  meetingId: string;
  systemInstruction: string;
  enabled: boolean;
  /** Language the AI client speaks — tags injected transcript lines. Defaults to "en". */
  language?: LangCode;
}

export interface GeminiLiveHandle {
  connect: () => void;
  disconnect: () => void;
  sendAudio: (pcm: Int16Array) => void;
  connected: boolean;
  error: string | null;
}

let lineCounter = 0;
function makeTimestamp(): string {
  const s = Math.floor(lineCounter++ * 2);
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

function int16ToBase64(pcm: Int16Array): string {
  const bytes = new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) {
    bin += String.fromCharCode(bytes[i]!);
  }
  return btoa(bin);
}

function base64ToFloat32(b64: string): Float32Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const int16 = new Int16Array(bytes.buffer);
  const float32 = new Float32Array(int16.length);
  for (let i = 0; i < int16.length; i++) {
    float32[i] = int16[i]! / 32768;
  }
  return float32;
}

export function useGeminiLive(opts: UseGeminiLiveOptions): GeminiLiveHandle {
  const { meetingId, systemInstruction, enabled: _enabled, language = "en" } = opts;
  const wsRef = useRef<WebSocket | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const nextPlayTimeRef = useRef(0);
  // Currently scheduled/playing audio sources, so barge-in can stop them.
  const activeSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const meetingIdRef = useRef(meetingId);
  const systemInstructionRef = useRef(systemInstruction);
  const languageRef = useRef(language);
  const textBufferRef = useRef("");
  const connectingRef = useRef(false);

  useEffect(() => {
    meetingIdRef.current = meetingId;
  }, [meetingId]);
  useEffect(() => {
    systemInstructionRef.current = systemInstruction;
  }, [systemInstruction]);
  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  const ensureAudioCtx = useCallback(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new AudioContext({ sampleRate: 24000 });
    }
    if (audioCtxRef.current.state === "suspended") {
      void audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }, []);

  const playAudioChunk = useCallback(
    (b64: string) => {
      const ctx = ensureAudioCtx();
      const samples = base64ToFloat32(b64);
      if (samples.length === 0) return;

      const buf = ctx.createBuffer(1, samples.length, 24000);
      buf.copyToChannel(samples, 0);
      const source = ctx.createBufferSource();
      source.buffer = buf;
      source.connect(ctx.destination);

      const now = ctx.currentTime;
      const startAt = Math.max(now, nextPlayTimeRef.current);
      source.start(startAt);
      nextPlayTimeRef.current = startAt + buf.duration;

      // Track so a barge-in (serverContent.interrupted) can stop everything
      // that's still queued — otherwise buffered AI speech keeps playing after
      // the rep starts talking, which reads as the AI lagging / talking over.
      activeSourcesRef.current.add(source);
      source.onended = () => {
        activeSourcesRef.current.delete(source);
      };
    },
    [ensureAudioCtx],
  );

  // Stop and drop all queued/playing AI audio. Called on barge-in and disconnect.
  const stopPlayback = useCallback(() => {
    for (const source of activeSourcesRef.current) {
      try {
        source.onended = null;
        source.stop();
      } catch {
        /* already stopped */
      }
    }
    activeSourcesRef.current.clear();
    nextPlayTimeRef.current = 0;
  }, []);

  const flushTextBuffer = useCallback(() => {
    const text = textBufferRef.current.trim();
    if (!text) return;
    textBufferRef.current = "";

    const line: TranscriptLine = {
      id: crypto.randomUUID(),
      t: makeTimestamp(),
      speaker: "client",
      name: "Client (AI)",
      lang: languageRef.current,
      text,
      isFinal: true,
    };

    void api(`/meetings/${meetingIdRef.current}/transcript`, {
      method: "POST",
      body: line,
    }).catch((err) => {
      console.warn("[gemini-live] transcript inject failed:", err);
    });
  }, []);

  const connectWithModel = useCallback(
    async (modelIdx: number) => {
      let key = effectiveGeminiKey();
      if (!key) {
        key = await ensureGeminiKey();
      }
      if (!key) {
        setError("Could not load Gemini API key. Please check connection.");
        return;
      }
      const entry = LIVE_MODELS[modelIdx];
      if (!entry) {
        connectingRef.current = false;
        setError("All Gemini Live models failed. Check your API key.");
        return;
      }

      const url = `wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.${entry.ver}.GenerativeService.BidiGenerateContent?key=${encodeURIComponent(key)}`;
      console.log(`[gemini-live] attempt #${modelIdx + 1}: ${entry.model} on ${entry.ver}`);

      // Timeout: if no setupComplete within 8s, try next model
      const timeout = setTimeout(() => {
        console.warn(`[gemini-live] timeout on ${entry.model}, trying next…`);
        ws.close();
        connectWithModel(modelIdx + 1);
      }, 8000);

      const ws = new WebSocket(url);
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;
      setError(null);

      ws.onopen = () => {
        console.log(`[gemini-live] WebSocket opened for ${entry.model}`);
        const setup = {
          setup: {
            model: entry.model,
            generationConfig: {
              responseModalities: ["AUDIO"],
              speechConfig: {
                voiceConfig: { prebuiltVoiceConfig: { voiceName: "Aoede" } },
              },
            },
            systemInstruction: {
              parts: [
                {
                  text:
                    systemInstructionRef.current ||
                    "You are a helpful client in a sales meeting simulation. Respond naturally to the sales representative.",
                },
              ],
            },
          },
        };
        ws.send(JSON.stringify(setup));
        console.log("[gemini-live] setup message sent");
      };

      let audioChunkCount = 0;
      const decoder = new TextDecoder();
      ws.onmessage = (ev) => {
        try {
          const raw =
            typeof ev.data === "string" ? ev.data : decoder.decode(ev.data as ArrayBuffer);
          const msg = JSON.parse(raw);

          if (msg.setupComplete) {
            clearTimeout(timeout);
            connectingRef.current = false;
            setConnected(true);
            setError(null);
            console.log(`[gemini-live] ✓ connected to ${entry.model}`);
            return;
          }

          // Barge-in: the rep started talking over the model. Drop everything
          // still queued so playback doesn't lag behind the conversation, and
          // discard the partial text for the cut-off turn.
          if (msg.serverContent?.interrupted) {
            console.log("[gemini-live] interrupted — flushing queued audio");
            stopPlayback();
            textBufferRef.current = "";
            return;
          }

          // Handle audio data from model
          const parts = msg.serverContent?.modelTurn?.parts;
          if (parts) {
            for (const part of parts) {
              if (part.inlineData?.data) {
                audioChunkCount++;
                if (audioChunkCount <= 3) {
                  console.log(
                    `[gemini-live] audio chunk #${audioChunkCount}, size=${part.inlineData.data.length}, mime=${part.inlineData.mimeType || "?"}`,
                  );
                }
                playAudioChunk(part.inlineData.data);
              }
              if (part.text) {
                textBufferRef.current += part.text;
              }
            }
          }

          // Native audio models send transcription separately
          if (msg.serverContent?.outputTranscription?.text) {
            textBufferRef.current += msg.serverContent.outputTranscription.text;
          }

          if (msg.serverContent?.turnComplete) {
            console.log(
              `[gemini-live] turn complete, audio chunks=${audioChunkCount}, text="${textBufferRef.current.slice(0, 80)}"`,
            );
            flushTextBuffer();
          }
        } catch (e) {
          console.warn("[gemini-live] message parse error:", e);
        }
      };

      ws.onerror = (ev) => {
        console.warn("[gemini-live] WebSocket error:", ev);
      };

      ws.onclose = (ev) => {
        clearTimeout(timeout);
        console.log(`[gemini-live] WebSocket closed: code=${ev.code} reason="${ev.reason}"`);
        setConnected(false);

        if (ev.code !== 1000 && connectingRef.current) {
          console.warn(`[gemini-live] ${entry.model} failed, trying next…`);
          connectWithModel(modelIdx + 1);
          return;
        }

        if (ev.code !== 1000) {
          const detail = ev.reason || `code ${ev.code}`;
          setError(`Gemini Live disconnected (${detail}).`);
        }
      };
    },
    [playAudioChunk, flushTextBuffer, ensureAudioCtx, stopPlayback],
  );

  const connect = useCallback(() => {
    ensureAudioCtx();
    connectingRef.current = true;
    connectWithModel(0);
  }, [connectWithModel, ensureAudioCtx]);

  const disconnect = useCallback(() => {
    connectingRef.current = false;
    if (wsRef.current) {
      wsRef.current.close(1000);
      wsRef.current = null;
    }
    setConnected(false);
    flushTextBuffer();
    stopPlayback();
    if (audioCtxRef.current) {
      void audioCtxRef.current.close();
      audioCtxRef.current = null;
    }
  }, [flushTextBuffer, stopPlayback]);

  const sendAudio = useCallback((pcm: Int16Array) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    // Newer Live API (gemini-3.x): realtimeInput.audio (Blob). The old
    // realtimeInput.mediaChunks is deprecated and now closes the socket.
    const msg = {
      realtimeInput: {
        audio: {
          mimeType: "audio/pcm;rate=16000",
          data: int16ToBase64(pcm),
        },
      },
    };
    ws.send(JSON.stringify(msg));
  }, []);

  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000);
        wsRef.current = null;
      }
      if (audioCtxRef.current) {
        void audioCtxRef.current.close();
        audioCtxRef.current = null;
      }
    };
  }, []);

  return { connect, disconnect, sendAudio, connected, error };
}
